import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Phone, X, UserRound, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const LS_KEY = 'abwesenheit_hinweis_dismissed';
let cache = { at: 0, data: null, token: null };

export const useAbwesenheitHinweise = () => {
  const token = localStorage.getItem('token');
  const [data, setData] = useState(cache.token === token ? cache.data : null);
  useEffect(() => {
    let alive = true;
    if (cache.data && cache.token === token && Date.now() - cache.at < 5 * 60 * 1000) { setData(cache.data); return undefined; }
    setData(null);
    fetch(`${API}/api/abwesenheiten/kunde/hinweise`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) { cache = { at: Date.now(), data: d, token }; if (alive) setData(d); } })
      .catch(() => {});
    return () => { alive = false; };
  }, [token]);
  return data;
};

const Avatar = ({ h }) => (
  <span className="relative shrink-0 w-11 h-11 rounded-full ring-2 ring-brand-gold/70 bg-white overflow-hidden inline-flex items-center justify-center">
    {h?.photo_url ? <img src={`${API}${h.photo_url}`} alt="" className="w-full h-full object-cover" /> : h ? <UserRound className="w-6 h-6 text-brand-navy/50" /> : <Users className="w-6 h-6 text-brand-navy/50" />}
    <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-brand-gold text-white flex items-center justify-center ring-2 ring-white"><Clock className="w-3 h-3" /></span>
  </span>
);

const TelefonPill = ({ nummer }) => nummer ? (
  <a href={`tel:${nummer.replace(/[^\d+]/g, '')}`} data-testid="abwesenheit-zentrale-tel"
    className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-full bg-brand-navy text-white text-xs font-semibold hover:bg-[#22456e] transition-colors">
    <Phone className="w-3.5 h-3.5" /> Zentrale: {nummer}
  </a>
) : null;

// Reine Darstellung: eine Hinweiszeile je abwesendem Mitarbeiter + optional Team-Hinweis
export const AbwesenheitHinweisKarte = ({ data, titel = 'Hinweis zur Erreichbarkeit', intro, onDismiss, compact = false, testId = 'abwesenheit-hinweis' }) => {
  if (!data || (!data.hinweise?.length && !data.team_hinweis)) return null;
  return (
    <div data-testid={testId}
      className={`relative overflow-hidden rounded-2xl border border-brand-gold/50 bg-[#fbf6e9] text-brand-navy shadow-sm ${compact ? 'p-4' : 'p-5 sm:p-6'}`}>
      <div className="absolute inset-y-0 left-0 w-1.5 bg-brand-gold" aria-hidden />
      <div className="pl-2">
        <div className="flex items-start justify-between gap-3">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-gold">{titel}</p>
          {onDismiss && (
            <button type="button" onClick={onDismiss} aria-label="Hinweis ausblenden" data-testid={`${testId}-dismiss`}
              className="-mt-1 -mr-1 w-8 h-8 rounded-lg flex items-center justify-center text-brand-navy/50 hover:text-brand-navy hover:bg-brand-gold/20">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {intro && <p className="mt-1.5 text-sm font-semibold leading-snug">{intro}</p>}
        <div className={`mt-3 space-y-3 ${compact ? '' : 'sm:space-y-4'}`}>
          {(data.hinweise || []).map(h => (
            <div key={h.id} className="flex items-start gap-3" data-testid={`abwesenheit-hinweis-${h.user_id}`}>
              <Avatar h={h} />
              <div className="min-w-0">
                <p className="text-sm leading-relaxed">{h.text}</p>
                <TelefonPill nummer={h.zentrale_telefon} />
              </div>
            </div>
          ))}
          {data.team_hinweis && (
            <div className="flex items-start gap-3" data-testid="abwesenheit-team-hinweis">
              <Avatar h={null} />
              <div className="min-w-0">
                <p className="text-sm leading-relaxed">{data.team_hinweis.text}</p>
                <TelefonPill nummer={data.team_hinweis.zentrale_telefon} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Portalweit unter dem Menü (nur Kunden), pro Tag und Stand einmal ausblendbar
export const AbwesenheitBanner = () => {
  const data = useAbwesenheitHinweise();
  const [hidden, setHidden] = useState(false);
  if (!data || (!data.hinweise?.length && !data.team_hinweis)) return null;
  const key = `${new Date().toISOString().slice(0, 10)}|${data.rev}`;
  if (hidden || localStorage.getItem(LS_KEY) === key) return null;
  const dismiss = () => { localStorage.setItem(LS_KEY, key); setHidden(true); };
  return (
    <div className="max-w-5xl mx-auto w-full px-4 lg:px-6 pt-4" data-testid="abwesenheit-banner">
      <AbwesenheitHinweisKarte data={data} onDismiss={dismiss} compact />
    </div>
  );
};

// Im Vorgang: nur die Abwesenheit der dort zuständigen Mitarbeiter
export const AbwesenheitVorgangHinweis = ({ ticketId, isStaff }) => {
  const [hinweise, setHinweise] = useState([]);
  useEffect(() => {
    if (!ticketId) return undefined;
    let alive = true;
    fetch(`${API}/api/abwesenheiten/vorgang/${ticketId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive) setHinweise(d?.hinweise || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [ticketId]);
  if (!hinweise.length) return null;
  if (isStaff) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="abwesenheit-vorgang-staff">
        <Clock className="w-4 h-4 flex-shrink-0" />
        <span>Zuständig, aber abwesend: <strong>{hinweise.map(h => h.name).join(', ')}</strong> – der Kunde sieht im Vorgang einen Hinweis zur Verzögerung.</span>
        <Link to="/portal/admin/abwesenheiten" className="ml-auto underline whitespace-nowrap">Abwesenheiten</Link>
      </div>
    );
  }
  return <AbwesenheitHinweisKarte data={{ hinweise }} titel="Hinweis zur Bearbeitung dieses Vorgangs" compact testId="abwesenheit-vorgang-hinweis" />;
};

export default AbwesenheitBanner;
